import * as microsoftTeams from '@microsoft/teams-js';
import { MEGABYTE } from './constants';
import { formatBytes } from './fileExplorer';
import { logger } from './logger';

// Receives drag-and-dropped files from the Teams host without teams-js, whose
// chunk assembly blocks the main thread for minutes on large files

// Silence on the chunk stream after which the receive counts as dead
const STALL_TIMEOUT_MS = 60 * 1000;
// Time the host gets to send the first chunk before falling back to the SDK
const FIRST_CHUNK_TIMEOUT_MS = 10 * 1000;

export type DndReceiveStats = {
    chunks: number;
    receivedBytes: number;
    elapsedMs: number;
    firstChunkBytes: number;
};

export class DndReceiveError extends Error {
    constructor(
        message: string,
        readonly stats: DndReceiveStats,
    ) {
        super(message);
        this.name = 'DndReceiveError';
    }
}

export type DndReceiveProgress = {
    receivedBytes: number;
    // Name of the file currently being received, the host sends it with every chunk
    fileName?: string;
};

type DndFileReceiverArgs = {
    // replyToId + threadId, the key Teams cached the dropped files under
    uniqueId: string;
    onProgress: (progress: DndReceiveProgress) => void;
    // Rejects when the host does not start sending within this time
    firstChunkTimeoutMs: number;
    // Rejects when the stream goes silent for this long after it started
    stallTimeoutMs: number;
};

// FileResult the host posts per chunk, see thirdPartyCloudStorage in teams-js
type FileResultMessage = {
    error?: { errorCode?: number; message?: string };
    fileChunk?: {
        chunk?: unknown;
        chunkSequence?: unknown;
        endOfFile?: unknown;
    };
    fileType?: unknown;
    fileName?: unknown;
    isLastFile?: unknown;
};

type HostResponse = {
    id?: unknown;
    uuidAsString?: unknown;
    args?: unknown[];
};

type RequestIds = { id: number; uuid: string };

const GET_DRAG_AND_DROP_FILES = 'thirdPartyCloudStorage.getDragAndDropFiles';

// Decoded chunks are grouped into Blobs of this size to keep the JS heap small
const BLOB_GROUP_BYTES = 8 * MEGABYTE;

// Ids outside the teams-js counter so its response router ignores the replies
const createRequestIds = (): RequestIds => ({
    id: Number.MAX_SAFE_INTEGER - Math.floor(Math.random() * 1e9),
    uuid: crypto.randomUUID(),
});

// Same wire format teams-js sendMessageToParent uses
const postGetDragAndDropFiles = (
    uniqueId: string,
    { id, uuid }: RequestIds,
): void => {
    let targetOrigin = '*';
    try {
        targetOrigin = new URL(document.referrer).origin;
    } catch {
        // no referrer, the host validates our origin on its side anyway
    }
    window.parent.postMessage(
        {
            id,
            uuidAsString: uuid,
            func: GET_DRAG_AND_DROP_FILES,
            timestamp: Date.now(),
            monotonicTimestamp: performance.now(),
            args: [uniqueId],
            apiVersionTag: `v2_${GET_DRAG_AND_DROP_FILES}`,
            isProxiedFromChild: false,
            teamsJsInstanceId: uuid,
        },
        targetOrigin,
    );
};

const isResponseTo = ({ id, uuid }: RequestIds, data: unknown): boolean => {
    const response = data as HostResponse | undefined;
    return response?.id === id || response?.uuidAsString === uuid;
};

const readFileResult = (data: unknown): FileResultMessage | undefined => {
    const result = (data as HostResponse | undefined)?.args?.[0];
    if (typeof result !== 'object' || result === null) {
        return undefined;
    }
    const candidate = result as FileResultMessage;
    const hasChunk = typeof candidate.fileChunk?.chunk === 'string';
    const hasFileError =
        candidate.error !== undefined &&
        ('fileType' in candidate ||
            'isLastFile' in candidate ||
            'fileChunk' in candidate);
    return hasChunk || hasFileError ? candidate : undefined;
};

const decodeBase64 = (chunk: string): Uint8Array => {
    const binary = atob(chunk);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
        bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
};

const startDndFileReceiver = ({
    uniqueId,
    onProgress,
    firstChunkTimeoutMs,
    stallTimeoutMs,
}: DndFileReceiverArgs): {
    files: Promise<File[]>;
    stop: () => DndReceiveStats;
} => {
    const startedAt = Date.now();
    const request = createRequestIds();
    const stats: DndReceiveStats = {
        chunks: 0,
        receivedBytes: 0,
        elapsedMs: 0,
        firstChunkBytes: 0,
    };
    const getStats = (): DndReceiveStats => ({
        ...stats,
        elapsedMs: Date.now() - startedAt,
    });

    let resolveFiles!: (files: File[]) => void;
    let rejectFiles!: (error: Error) => void;
    const files = new Promise<File[]>((resolve, reject) => {
        resolveFiles = resolve;
        rejectFiles = reject;
    });

    const receivedFiles: File[] = [];
    let parts: Uint8Array[] = [];
    let partsBytes = 0;
    let blobs: Blob[] = [];
    let previousSequence: number | undefined;
    let stopped = false;
    let stallTimer: ReturnType<typeof setTimeout> | undefined;

    const stop = (): DndReceiveStats => {
        if (!stopped) {
            stopped = true;
            clearTimeout(stallTimer);
            window.removeEventListener('message', onMessage, true);
        }
        return getStats();
    };

    const fail = (reason: string): void => {
        rejectFiles(new DndReceiveError(reason, stop()));
    };

    const armTimer = (timeoutMs: number): void => {
        clearTimeout(stallTimer);
        stallTimer = setTimeout(
            () => fail(`no chunk received for ${timeoutMs}ms`),
            timeoutMs,
        );
    };

    const flushParts = (type: string): void => {
        if (parts.length > 0) {
            blobs.push(new Blob(parts, { type }));
            parts = [];
            partsBytes = 0;
        }
    };

    const onMessage = (event: MessageEvent): void => {
        if (event.source !== window.parent && event.source !== window.top) {
            return;
        }
        if (!isResponseTo(request, event.data)) {
            return;
        }
        const result = readFileResult(event.data);
        if (!result) {
            return;
        }
        event.stopImmediatePropagation();

        if (result.error) {
            fail(
                result.error.message ?? `error code ${result.error.errorCode}`,
            );
            return;
        }

        const sequence = result.fileChunk?.chunkSequence;
        if (
            typeof sequence === 'number' &&
            previousSequence !== undefined &&
            sequence !== previousSequence + 1
        ) {
            fail(`chunk ${sequence} received after ${previousSequence}`);
            return;
        }
        previousSequence = typeof sequence === 'number' ? sequence : undefined;

        const fileType =
            typeof result.fileType === 'string' ? result.fileType : '';
        const fileName =
            typeof result.fileName === 'string' ? result.fileName : undefined;
        const bytes = decodeBase64(result.fileChunk?.chunk as string);
        stats.chunks += 1;
        stats.receivedBytes += bytes.length;
        if (stats.chunks === 1) {
            stats.firstChunkBytes = bytes.length;
        }
        parts.push(bytes);
        partsBytes += bytes.length;
        if (partsBytes >= BLOB_GROUP_BYTES) {
            flushParts(fileType);
        }

        // Flags are only checked for truthiness, like teams-js does
        if (result.fileChunk?.endOfFile) {
            flushParts(fileType);
            receivedFiles.push(
                new File(
                    blobs,
                    fileName ?? `file-${receivedFiles.length + 1}`,
                    {
                        type: fileType,
                    },
                ),
            );
            blobs = [];
            previousSequence = undefined;
            if (result.isLastFile) {
                stop();
                resolveFiles(receivedFiles);
                return;
            }
        }

        armTimer(stallTimeoutMs);
        onProgress({ receivedBytes: stats.receivedBytes, fileName });
    };

    // Listen before asking so no chunk is missed
    window.addEventListener('message', onMessage, true);
    armTimer(firstChunkTimeoutMs);
    postGetDragAndDropFiles(uniqueId, request);

    return { files, stop };
};

export const describeDndReceive = ({
    chunks,
    receivedBytes,
    elapsedMs,
    firstChunkBytes,
}: DndReceiveStats): string =>
    `${chunks} chunks of ${formatBytes(firstChunkBytes, 0)}, ${formatBytes(
        receivedBytes,
        0,
    )} in ${Math.round(elapsedMs / 1000)}s`;

const isSupportedByHost = (): boolean => {
    try {
        return microsoftTeams.thirdPartyCloudStorage.isSupported();
    } catch {
        return false;
    }
};

// Fallback for hosts that do not answer the direct request, slow on large files
const receiveViaSdk = (uniqueId: string): Promise<File[]> =>
    new Promise((resolve, reject) => {
        try {
            microsoftTeams.thirdPartyCloudStorage.getDragAndDropFiles(
                uniqueId,
                (files, error) => {
                    if (error) {
                        reject(
                            new Error(
                                error.message ??
                                    `error code ${error.errorCode}`,
                            ),
                        );
                        return;
                    }
                    resolve(files as File[]);
                },
            );
        } catch (err) {
            reject(err);
        }
    });

const toDndReceiveError = (
    err: unknown,
    stats: DndReceiveStats,
): DndReceiveError =>
    err instanceof DndReceiveError
        ? err
        : new DndReceiveError(
              err instanceof Error ? err.message : String(err),
              stats,
          );

export type DndReceiveResult = { files: File[]; stats: DndReceiveStats };

// Rejects with a DndReceiveError only
export const receiveDndFiles = async ({
    uniqueId,
    onProgress,
}: {
    uniqueId: string;
    onProgress: (progress: DndReceiveProgress) => void;
}): Promise<DndReceiveResult> => {
    if (!isSupportedByHost()) {
        throw new DndReceiveError('thirdPartyCloudStorage not supported', {
            chunks: 0,
            receivedBytes: 0,
            elapsedMs: 0,
            firstChunkBytes: 0,
        });
    }

    const receiver = startDndFileReceiver({
        uniqueId,
        onProgress,
        firstChunkTimeoutMs: FIRST_CHUNK_TIMEOUT_MS,
        stallTimeoutMs: STALL_TIMEOUT_MS,
    });

    try {
        const files = await receiver.files;
        return { files, stats: receiver.stop() };
    } catch (err) {
        const stats = receiver.stop();
        const error = toDndReceiveError(err, stats);
        if (stats.chunks > 0) {
            throw error;
        }

        // Nothing arrived for the direct request, let the SDK drive the transfer
        logger.warn(
            `getDragAndDropFiles: ${error.message}, falling back to the SDK`,
            'Files',
        );
        try {
            return { files: await receiveViaSdk(uniqueId), stats };
        } catch (sdkErr) {
            throw toDndReceiveError(sdkErr, stats);
        }
    }
};
